// Kanıt Belgeleri sekmesi (TODO-EVIDENCE-02) — /admin/settings/evidence.
// Üstte registry alanları (SettingsForm, Settings.tsx render eder); burada durum kartı:
// tarama sağlayıcısı + clamd sağlığı + bekleyen/engellenen/temizlenen sayaçları + retention
// dry-run önizlemesi (SİLMEZ — gerçek silme günlük bakım görevi ya da `evidence:purge --apply`).

import { useEffect, useState } from "react";
import type { EvidencePolicyStatus, EvidencePurgePreview } from "@mahir/shared";
import { api } from "../../api/client";

const ACTION_LABEL: Record<EvidencePurgePreview["planned"][number]["action"], string> = {
  delete_row: "Dosya + kayıt silinecek",
  purge_object: "Yalnız dosya silinecek (bağlar kalır)",
};

export function EvidencePolicyPanel(): JSX.Element {
  const [status, setStatus] = useState<EvidencePolicyStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<EvidencePurgePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    let aborted = false;
    api<EvidencePolicyStatus>("/admin/settings/evidence-status")
      .then((res) => {
        if (!aborted) setStatus(res);
      })
      .catch((err) => {
        if (!aborted) setError(err instanceof Error ? err.message : "Durum okunamadı");
      });
    return () => {
      aborted = true;
    };
  }, []);

  const runPreview = (): void => {
    setPreviewing(true);
    setError(null);
    api<EvidencePurgePreview>("/admin/settings/evidence-purge/preview", { method: "POST" })
      .then(setPreview)
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Önizleme alınamadı");
      })
      .finally(() => {
        setPreviewing(false);
      });
  };

  return (
    <section
      aria-label="Kanıt belgeleri — tarama ve temizlik durumu"
      style={{
        marginTop: 24,
        padding: 16,
        borderRadius: 10,
        border: "1px solid var(--color-border)",
        background: "var(--color-bg)",
      }}
    >
      <h2 style={{ fontSize: "0.95rem", fontWeight: 600, marginBottom: 8 }}>
        Tarama ve temizlik durumu
      </h2>
      <p style={{ fontSize: "0.78rem", color: "var(--color-text-3)", marginBottom: 12 }}>
        Yüklenen her kanıt dosyası (KPI kanıtları ve form ekleri) önce taranır; yalnız{" "}
        <strong>temiz</strong> dosya açılır. Engellenen dosya depodan silinir. Günlük bakım görevi
        saklama süresi dolan referanssız dosyaları ve tamamlanmış içe aktarmaların kaynak
        dosyalarını temizler (audit: <code>evidence.purged</code>). Sağlayıcı ve clamd adresi{" "}
        <code>SCAN_PROVIDER</code> / <code>CLAMAV_HOST</code> env ile ayarlanır.
      </p>
      {error && (
        <p role="alert" style={{ color: "var(--color-error)", fontSize: "0.8rem" }}>
          {error}
        </p>
      )}
      {status && (
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "max-content 1fr",
            columnGap: 16,
            rowGap: 6,
            fontSize: "0.82rem",
          }}
        >
          <dt style={{ color: "var(--color-text-3)" }}>Tarama sağlayıcısı</dt>
          <dd>
            {status.scanProvider === "clamav" ? "ClamAV (clamd)" : "Mock (yalnız geliştirme)"}
            {" · "}
            <span
              style={{
                color: status.scanner.ok ? "var(--color-success, #1a7f37)" : "var(--color-error)",
              }}
            >
              {status.scanner.ok
                ? "erişilebilir"
                : `erişilemiyor — ${status.scanner.message ?? ""}`}
            </span>
          </dd>
          <dt style={{ color: "var(--color-text-3)" }}>Saklama süreleri</dt>
          <dd>
            referanssız{" "}
            {status.retention.evidenceDays === 0
              ? "kapalı"
              : `${String(status.retention.evidenceDays)} gün`}
            {" · "}
            içe aktarma kaynağı{" "}
            {status.retention.importDays === 0
              ? "kapalı"
              : `${String(status.retention.importDays)} gün`}
            {status.retention.dryRun && " · DRY-RUN (env: yalnız loglar, silmez)"}
          </dd>
          <dt style={{ color: "var(--color-text-3)" }}>KPI kanıtları</dt>
          <dd>
            {String(status.counts.evidencePending)} taranıyor ·{" "}
            {String(status.counts.evidenceInfected)} engellendi ·{" "}
            {String(status.counts.evidencePurged)} temizlendi (kayıt kaldı)
          </dd>
          <dt style={{ color: "var(--color-text-3)" }}>Form ekleri</dt>
          <dd>
            {String(status.counts.attachmentsPending)} taranıyor ·{" "}
            {String(status.counts.attachmentsInfected)} engellendi
            <span style={{ color: "var(--color-text-3)" }}> (aynı kanıt havuzunda)</span>
          </dd>
          <dt style={{ color: "var(--color-text-3)" }}>Müşteri override'ı</dt>
          <dd style={{ color: "var(--color-text-2)" }}>
            Müşteriye özel saklama süresi ve gizlilik varsayılanı müşteri profilindeki{" "}
            <b>Kanıt politikası</b> kartından; dosya bazlı saklama (sistem / süresiz / N gün) hücre
            editörünün Kanıt sekmesinden ayarlanır.
          </dd>
        </dl>
      )}
      <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
        <button
          type="button"
          onClick={runPreview}
          disabled={previewing}
          className="rounded px-3 py-1.5 text-sm"
          style={{
            border: "1px solid var(--color-border)",
            background: "var(--color-surface)",
            color: "var(--color-text)",
          }}
        >
          {previewing ? "Hesaplanıyor…" : "Temizlik önizlemesi (silmez)"}
        </button>
        {preview && (
          <span style={{ fontSize: "0.8rem", color: "var(--color-text-2)" }}>
            {String(preview.scanned)} dosya tarandı · {String(preview.planned.length)} aday
          </span>
        )}
      </div>
      {preview && preview.planned.length > 0 && (
        <ul style={{ marginTop: 8, fontSize: "0.78rem", fontFamily: "var(--font-mono)" }}>
          {preview.planned.slice(0, 20).map((p) => (
            <li key={p.id}>
              {p.id.slice(0, 8)} · {ACTION_LABEL[p.action]} · {p.reason}
            </li>
          ))}
          {preview.planned.length > 20 && <li>… +{String(preview.planned.length - 20)} daha</li>}
        </ul>
      )}
    </section>
  );
}
